import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, storePublicBase } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useMerchantAuth } from "@/lib/auth";
import { Plus, Pencil, Trash2, Copy } from "lucide-react";

export default function AdminBuildBoxes() {
  const { tenant } = useMerchantAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/merchant/build-boxes")
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const copy = async (path) => {
    const url = `${storePublicBase(tenant).replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Box link copied");
    } catch {
      toast.error(url);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this box?")) return;
    try {
      await api.delete(`/merchant/build-boxes/${id}`);
      toast.success("Box deleted");
      load();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-build-boxes">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Build Your Own Box</h1>
          <p className="text-sm text-slate-500 mt-1">Set a price, a product count, and which products shoppers can put in the box.</p>
        </div>
        <Link to="/admin/build-boxes/new"><Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="build-box-new"><Plus className="w-4 h-4 mr-1" /> New box</Button></Link>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : null}
      <div className="space-y-3">
        {items.map((b) => (
          <Card key={b.id} className="p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`build-box-row-${b.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">{b.name}</div>
              <div className="text-sm text-slate-500">{b.item_count} products · ₹{Number(b.price || 0).toLocaleString("en-IN")} · {b.is_active ? "Live" : "Hidden"}</div>
              <div className="text-xs font-mono text-slate-600 mt-1 break-all" data-testid={`build-box-link-${b.id}`}>{b.public_path}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => copy(b.path)} data-testid={`build-box-copy-${b.id}`}><Copy className="w-4 h-4" /></Button>
              <Link to={`/admin/build-boxes/${b.id}`}><Button variant="outline" size="sm" data-testid={`build-box-edit-${b.id}`}><Pencil className="w-4 h-4" /></Button></Link>
              <Button variant="outline" size="sm" onClick={() => remove(b.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </Card>
        ))}
        {!loading && !items.length ? <p className="text-sm text-slate-500">No boxes yet.</p> : null}
      </div>
    </div>
  );
}
